import { useRef, useState, type DragEvent } from "react";

/** What can be dragged out of the editor's right column. */
export type SideDrag = { kind: "element"; blueprintId: number; sensorId?: number } | { kind: "file"; fileId: number; name: string };
export type DroppedFile = Extract<SideDrag, { kind: "file" }>;

// One drag at a time, and only inside this page: the payload needs no serialising, and a drop target must be able to answer
// "can I take this?" during dragover, when the browser hides the data transfer's content.
let current: SideDrag | null = null;
// Only one target shows as the one under the pointer: an inner target stops the event, so the step around it would keep its ring.
let clearActive: (() => void) | null = null;
const setActive = (clear: (() => void) | null) => {
  if (clearActive !== clear) clearActive?.();
  clearActive = clear;
};

/** Native drag and drop: the column and the steps sit in different trees. A click on the same tile or row keeps working, for keyboard and touch. */
export function sideDragProps(payload: SideDrag, onStart?: () => void) {
  return {
    draggable: true,
    onDragStart: (e: DragEvent) => {
      current = payload;
      e.dataTransfer.effectAllowed = "copy";
      e.dataTransfer.setData("application/x-arc-side-drag", payload.kind); // Firefox starts no drag without data
      onStart?.();
    },
    onDragEnd: () => {
      current = null;
      setActive(null);
    },
  };
}

/** `types` holds extensions, the same lists a pick filters by. */
export function fileMatches(name: string, types: readonly string[] | null | undefined): boolean {
  if (!types) return true;
  const lower = name.toLowerCase();
  return types.some((t) => lower.endsWith(t.toLowerCase()));
}

/**
 * A drop target. A drag it does not accept is left alone, so the browser shows "not allowed" and a target further out can take it.
 * With `blockRefused` a refused drag goes no further: an image on a 3D-model field must not land on the step around that field.
 */
export function useSideDrop(accepts: (drag: SideDrag) => boolean, onDrop: (drag: SideDrag) => void, { blockRefused = false }: { blockRefused?: boolean } = {}) {
  const [isOver, setIsOver] = useState(false);
  const clear = useRef(() => setIsOver(false)).current;
  /** Handles the refusal; `false` means the event is this target's to take. */
  const refused = (e: DragEvent) => {
    if (current == null) return true;
    if (accepts(current)) return false;
    if (blockRefused) {
      e.stopPropagation();
      setActive(null);
    }
    return true;
  };
  const over = (e: DragEvent) => {
    if (refused(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setActive(clear);
    setIsOver(true);
  };
  return {
    isOver,
    dropProps: {
      onDragEnter: over,
      onDragOver: over,
      onDragLeave: (e: DragEvent) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        if (clearActive === clear) setActive(null);
        else setIsOver(false);
      },
      onDrop: (e: DragEvent) => {
        if (refused(e) || current == null) return;
        e.preventDefault();
        e.stopPropagation();
        const drag = current;
        current = null;
        setActive(null);
        setIsOver(false);
        onDrop(drag);
      },
    },
  };
}
